"use client";

/**
 * AlertsOnboardingGate — decides WHEN the onboarding VIDEO modal shows.
 *
 * v5 (2026-07-17): the 6-step OnboardingTourHub checklist is replaced by
 * `VideoOnboardingModal` (auto-playing user-story walkthrough). Dismissal is
 * now DB-BACKED: closing the modal (X or "Don't show this again") writes the
 * sticky `users.alerts_onboarding.completed=true` flag via the existing
 * `/onboarding/alerts-progress` endpoint, and the auto-open gate first asks
 * `/onboarding/alerts-status` for `should_auto_open`. localStorage is only a
 * fast-path cache — incognito / new devices can't resurrect the modal.
 *
 * Behaviour (post-v4, manual-open + one-shot intro auto-open):
 *   - Mounts on every route for signed-in users; renders nothing for
 *     signed-out visitors (the hub is a logged-in surface only).
 *   - Manual open: any code path can fire
 *     `xynth:open-alerts-onboarding` to pop the hub. Today this is
 *     dispatched by the "Onboarding to Xynth" tile on `/automations`.
 *   - Auto-open intro (post-rollout): the hub auto-pops EXACTLY ONCE
 *     per uid when the user lands on bare `/` (no query string) and
 *     the questionnaire signal has settled. This is the rollout-day
 *     introduction for users who already exist when the new tour
 *     system ships. Gated by `xynth.tour_hub_introduced:<uid>` —
 *     once the user closes the hub, the flag is written and the
 *     auto-open never repeats. Manual tile-clicks ignore the flag
 *     so re-opens always work. See the auto-open effect below for
 *     the full condition list.
 *   - The questionnaire is still the canonical "first onboarding" —
 *     it fires from `OnboardingManager` after the user enters their
 *     email-verification code (post-verify redirect lands on
 *     `/?onboarding_start=1`). The hub auto-open intentionally waits
 *     for the questionnaire to settle (completed marker OR dismissed)
 *     so the two never stack.
 *
 * Progress is stored client-side in localStorage (see
 * `lib/onboarding-tour.ts`). The legacy `ALERTS_ONBOARDING_UPDATED_EVENT`
 * is still emitted (with `max_slide_seen` now meaning completed stage
 * count and `total = 5`) so the thumbnail card progress badge keeps
 * flipping in lockstep.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { API_BASE_URL } from "@/lib/config";
import { telemetry } from "@/lib/telemetry";
import { VideoOnboardingModal } from "./VideoOnboardingModal";
import {
  ONBOARDING_VIDEO_ID,
  hasSeenOnboardingVideo,
  markOnboardingVideoSeen,
} from "@/lib/videos-catalog";
import {
  CORE_TOUR_STAGE_IDS,
  TOUR_PROGRESS_UPDATED_EVENT,
  TOUR_STAGE_COMPLETE_EVENT,
  TOUR_TOTAL,
  markTourStageComplete,
  readTourProgress,
  type TourStageCompleteDetail,
} from "@/lib/onboarding-tour";

// Event the Automations tab fires to re-open the modal on demand.
export const OPEN_ALERTS_ONBOARDING_EVENT = "xynth:open-alerts-onboarding";

// Event the gate fires AFTER every successful progress write so the
// Automations tutorial card can flip state (e.g. → Completed) instantly
// without waiting for its 20s poll or a focus event.
//
// `detail` carries the server's canonical response so listeners can
// apply optimistic updates:
//   { max_slide_seen: number, total: number, completed: boolean }
export const ALERTS_ONBOARDING_UPDATED_EVENT =
  "xynth:alerts-onboarding-updated";

export function AlertsOnboardingGate() {
  const { user, emailVerified } = useAuth();
  const uid = user?.uid ?? null;
  const shouldMount = Boolean(uid);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [open, setOpen] = useState(false);
  // Hard same-mount dismissal latch. Once the user closes the modal we NEVER
  // re-open it in this page lifetime, no matter what storage / effect re-runs
  // do. This is the belt-and-braces fix for "I closed it and it came back".
  const dismissedRef = useRef(false);

  // ── Manual open via event (clicking the Onboarding tile, etc.) ───
  useEffect(() => {
    if (!shouldMount) return;
    const handler = () => setOpen(true);
    window.addEventListener(OPEN_ALERTS_ONBOARDING_EVENT, handler);
    return () =>
      window.removeEventListener(OPEN_ALERTS_ONBOARDING_EVENT, handler);
  }, [shouldMount]);

  // ── One-time auto-open introduction ───────────────────────────────
  // Pops the onboarding video exactly once per uid, DB-gated.
  //
  // Auto-open conditions (ALL must be true):
  //   1. Signed-in + email-verified.
  //   2. Not dismissed in this page lifetime (dismissedRef).
  //   3. localStorage seen-cache not set (fast path; incognito-safe
  //      because the DB check below is the real gate).
  //   4. Pathname is exactly `/` with NO query params — any query
  //      string means the user is mid-flow (questionnaire, replay
  //      return, finale tour, …) and we never stack modals.
  //   5. The questionnaire signal has settled — localStorage
  //      `onboarding_status_checked:<uid>="completed"` OR the user
  //      explicitly dismissed the questionnaire.
  //   6. THE DB SAYS SO: `GET /onboarding/alerts-status` returns
  //      `should_auto_open=true` (users.alerts_onboarding is fresh —
  //      never seen ANY onboarding modal, old deck or new video).
  //      Closing the modal writes the sticky completed flag back, so
  //      this survives incognito, new devices, cleared storage.
  //      If the fetch fails we DON'T show (fail closed — a popup that
  //      can't record its own dismissal must never fire).
  useEffect(() => {
    if (!shouldMount || !uid || !emailVerified) return;
    if (dismissedRef.current) return;
    if (pathname !== "/") return;
    const queryString = searchParams?.toString() ?? "";
    if (queryString.length > 0) return;
    if (hasSeenOnboardingVideo(uid)) return;

    const MAX_TRIES = 20;       // 20 × 250ms ≈ 5s settle window (slow first
                                // loads: the questionnaire status fetch must
                                // land before we're willing to pop anything)
    const POLL_MS = 250;
    const GRACE_MS = 600;       // post-settle delay before showing
    let tries = 0;
    let cancelled = false;
    let pollHandle: number | null = null;
    let graceHandle: number | null = null;

    const tryOpen = () => {
      pollHandle = null;
      // Re-read everything in case state changed during the wait.
      if (cancelled || dismissedRef.current) return;
      if (hasSeenOnboardingVideo(uid)) return;
      if (open) return; // modal already showing
      const completedMarker =
        window.localStorage.getItem(`onboarding_status_checked:${uid}`) ===
        "completed";
      const dismissed =
        window.localStorage.getItem(`onboarding_questionnaire_dismissed:${uid}`) ===
        "true";
      if (completedMarker || dismissed) {
        // Questionnaire settled → ask the DB whether this user has ever
        // seen/dismissed the onboarding modal (any version).
        (async () => {
          try {
            const res = await fetch(
              `${API_BASE_URL}/onboarding/alerts-status?user_id=${encodeURIComponent(uid)}`,
            );
            if (!res.ok) return; // fail closed
            const data = await res.json();
            if (cancelled || dismissedRef.current) return;
            if (!data?.should_auto_open) {
              // DB says already seen — backfill the local cache so we
              // skip the network round-trip on future page loads.
              markOnboardingVideoSeen(uid);
              return;
            }
            graceHandle = window.setTimeout(() => {
              graceHandle = null;
              if (cancelled || dismissedRef.current) return;
              if (hasSeenOnboardingVideo(uid)) return;
              telemetry.emit("onboarding.video_open", {
                video_id: ONBOARDING_VIDEO_ID,
                trigger: "auto",
              });
              setOpen(true);
            }, GRACE_MS);
          } catch {
            // Network error → fail closed, try again next page load.
          }
        })();
        return;
      }
      tries += 1;
      if (tries < MAX_TRIES) {
        pollHandle = window.setTimeout(tryOpen, POLL_MS);
      }
      // Else give up silently — questionnaire is still in flight. The
      // user will see the video on a later bare-`/` visit.
    };

    pollHandle = window.setTimeout(tryOpen, POLL_MS);

    return () => {
      cancelled = true;
      if (pollHandle != null) window.clearTimeout(pollHandle);
      if (graceHandle != null) window.clearTimeout(graceHandle);
    };
  }, [shouldMount, uid, emailVerified, pathname, searchParams, open]);

  // Global stage-complete listener. The gate is mounted in app/layout.tsx
  // so it's alive on every route — meaning a `dispatchStageComplete()`
  // fired from /replay, /alerts, /automations, or /blog all flow through
  // here and get persisted to localStorage regardless of where the user
  // currently is.
  useEffect(() => {
    if (!uid) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<TourStageCompleteDetail>).detail;
      if (detail?.stage) markTourStageComplete(uid, detail.stage);
    };
    window.addEventListener(TOUR_STAGE_COMPLETE_EVENT, handler);
    return () =>
      window.removeEventListener(TOUR_STAGE_COMPLETE_EVENT, handler);
  }, [uid]);

  // Backfill the questionnaire stage for users who completed onboarding
  // BEFORE the tour-hub ever existed. Without this they'd see stage 1 as
  // "Not started" forever (since `dispatchStageComplete("questionnaire")`
  // only fires inside the live `completeOnboarding` flow, which they've
  // already passed). Fast path: `OnboardingManager` already caches a
  // `onboarding_status_checked:<uid> = "completed"` marker in localStorage
  // the first time it sees a completed user, so we can avoid the network
  // round-trip on every page load. Falls back to a single GET on cache
  // miss. Best-effort — failure is silent (the user can still re-do the
  // questionnaire if they want, it's a 1-min flow).
  useEffect(() => {
    if (!uid) return;
    if (readTourProgress(uid).questionnaire) return;

    const cached =
      typeof window !== "undefined"
        ? window.localStorage.getItem(`onboarding_status_checked:${uid}`)
        : null;
    if (cached === "completed") {
      markTourStageComplete(uid, "questionnaire");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/onboarding/status?user_id=${encodeURIComponent(uid)}`,
        );
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (data?.completed) {
          markTourStageComplete(uid, "questionnaire");
          // Mirror the manager's cache write so future loads short-
          // circuit the same way without each surface duplicating it.
          try {
            window.localStorage.setItem(
              `onboarding_status_checked:${uid}`,
              "completed",
            );
          } catch {
            /* private mode / quota — ignore */
          }
        }
      } catch {
        /* non-fatal — backfill is a quality-of-life nicety */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  // Bridge tour progress events to the legacy ALERTS_ONBOARDING_UPDATED
  // event so the thumbnail card on the Automations tab keeps flipping
  // its completion / progress badge without any changes.
  useEffect(() => {
    if (!shouldMount) return;
    const onUpdate = () => {
      if (!uid) return;
      const progress = readTourProgress(uid);
      // Only count core (5) stages — the optional `journey` row is a
      // conversion CTA, not a tour step, and must not push the count
      // past TOUR_TOTAL.
      const completedCount = CORE_TOUR_STAGE_IDS.reduce(
        (acc, id) => acc + (progress[id] ? 1 : 0),
        0,
      );
      window.dispatchEvent(
        new CustomEvent(ALERTS_ONBOARDING_UPDATED_EVENT, {
          detail: {
            max_slide_seen: completedCount,
            total: TOUR_TOTAL,
            completed: completedCount >= TOUR_TOTAL,
          },
        }),
      );
    };
    window.addEventListener(TOUR_PROGRESS_UPDATED_EVENT, onUpdate);
    return () =>
      window.removeEventListener(TOUR_PROGRESS_UPDATED_EVENT, onUpdate);
  }, [shouldMount, uid]);

  // ANY close (X, outside click, Escape, or the explicit "Don't show this
  // again" button) means "the user has seen the onboarding video" — latch it
  // everywhere: this mount (ref), this browser (localStorage cache), and the
  // ACCOUNT (DB, sticky `alerts_onboarding.completed=true`) so it never
  // auto-pops again on any device / incognito window.
  const handleClose = useCallback(
    (reason: "closed" | "dont_show_again" = "closed") => {
      dismissedRef.current = true;
      setOpen(false);
      if (!uid) return;
      markOnboardingVideoSeen(uid);
      telemetry.emit("onboarding.video_dismissed", {
        video_id: ONBOARDING_VIDEO_ID,
        reason,
      });
      // Fire-and-forget DB write — the sticky completed flag is what the
      // auto-open gate reads back on every future login.
      fetch(`${API_BASE_URL}/onboarding/alerts-progress`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: uid, slide_idx: 1, completed: true }),
      }).catch(() => {});
    },
    [uid],
  );

  if (!shouldMount) return null;

  return <VideoOnboardingModal open={open} onClose={handleClose} />;
}
